import { apiConnection } from "@/services/api-connection";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { storageManager } from "@/services/storage/storage-manager";
import { userManager } from "@/services/security/user-manager";
import { useUserStore } from "@/store/user/user";
import router from "@/router";
import { cookieManager } from "@/services/cookie/cookie-manager";
import { registrationConstante } from "@/constantes/registration";

const URL_SEND_NEW_PASSWORD = "/app/user/reset-password";
const URL_RESET_PASSWORD = "/app/user/ask-reset-password";

class LoginManager {
  token = "";
  refreshToken = "";
  tokenExpirationDate = null;
  stayConnected = 0;
  apiPath = "";
  authorizedLoginAttempts = 5;

  constructor() {
    this.reloadToken();
  }

  setApiPath(platform) {
    const url = "https://" + platform.hostname + "/api";
    const date = new Date();
    window.localStorage.setItem("studea-apiPathDate", date.toISOString());
    window.localStorage.setItem("studea-apiPath", url);
    window.sessionStorage.setItem("studea-apiPath", url);
    localStorage.setItem("studea-activateChatbot", platform.activateChatBot ? 1 : 0);
    this.apiPath = url;
  }

  getApiPath() {
    return window.localStorage.getItem("studea-apiPath")
      ? window.localStorage.getItem("studea-apiPath")
      : window.sessionStorage.getItem("studea-apiPath");
  }

  reloadToken() {
    this.token = this.getToken();
    this.refreshToken = this.getRefreshToken();
    this.stayConnected = this.getStayConnected();
    const localApiPath = this.getApiPath();
    if (typeof localApiPath == "string") {
      this.apiPath = localApiPath;
    }
  }

  setToken(res, apiPath, stayConnected = false) {
    this.token = res.token;
    this.refreshToken = res.refresh_token;
    this.tokenExpirationDate = res.expires;

    if (localStorage.getItem("studea-newTab") && localStorage.getItem("studea-newTab") === "1") {
      storageManager.setFromLocalStorage();
    } else {
      if (stayConnected) {
        window.localStorage.setItem("studea-refreshToken", res.refresh_token);
        window.localStorage.setItem("studea-token", res.token);
        window.localStorage.setItem("studea-tokenExpirationDate", res.expires);
        window.localStorage.setItem("studea-apiPath", apiPath);
        window.localStorage.setItem("studea-stayConnected", "1");
      }
      window.sessionStorage.setItem("studea-refreshToken", res.refresh_token);
      window.sessionStorage.setItem("studea-token", res.token);
      window.sessionStorage.setItem("studea-tokenExpirationDate", res.expires);
      window.sessionStorage.setItem("studea-apiPath", apiPath);
      window.sessionStorage.setItem("studea-sessionTS", Date.now());
    }
  }

  tokenIsValid() {
    const current = new Date();
    const storedDate = localStorage.getItem("studea-tokenExpirationDate");
    let expirationDate = null;
    if (storedDate) {
      expirationDate = new Date(localStorage.getItem("studea-tokenExpirationDate"));
    }

    return expirationDate && expirationDate.getTime() > current.getTime();
  }

  isToken() {
    return this.getToken() || this.getRefreshToken();
  }

  getToken() {
    if (window.sessionStorage.getItem("studea-switched") && window.sessionStorage.getItem("studea-switched") === "1") {
      return window.sessionStorage.getItem("studea-token");
    } else {
      return window.localStorage.getItem("studea-token")
        ? window.localStorage.getItem("studea-token")
        : window.sessionStorage.getItem("studea-token");
    }
  }

  getRefreshToken() {
    if (window.sessionStorage.getItem("studea-switched") && window.sessionStorage.getItem("studea-switched") === "1") {
      return window.sessionStorage.getItem("studea-refreshToken");
    } else {
      return window.localStorage.getItem("studea-refreshToken")
        ? window.localStorage.getItem("studea-refreshToken")
        : window.sessionStorage.getItem("studea-refreshToken");
    }
  }

  getStayConnected() {
    return parseInt(window.localStorage.getItem("studea-stayConnected"));
  }

  refreshConnection() {
    this.reloadToken();
    const credentials = {
      refresh_token: this.refreshToken,
    };
    const magicLink = apiConnection.getMagicLinkToken();

    return new Promise((resolve, reject) => {
      if (this.tokenIsValid()) {
        resolve({ success: true });
      } else if (this.refreshToken) {
        apiConnection
          .fetchApi("post", this.apiPath + "/token/refresh", credentials, false)
          .then((data) => {
            if (data.token) {
              this.setToken(data, this.apiPath, parseInt(window.localStorage.getItem("studea-stayConnected")));
              resolve({ success: true });
            } else {
              reject(data);
            }
          })
          .catch(function (error) {
            if (error.code === 401) {
              storageManager.clear(true);
              window.location.reload();
            }
          });
      } else if (!magicLink) {
        userManager.logout(true);
      }
    });
  }

  login(login, password, stayConnected = false) {
    const credentials = {
      username: login,
      password: password,
    };

    return new Promise((resolve) => {
      const attempts = parseInt(this.loginAttempts());
      if (attempts && attempts >= this.authorizedLoginAttempts) {
        resolve({ success: false, message: i18n.global.t("error.login_blocked"), messageType: "warning" });
      } else {
        apiConnection
          .fetchApi("post", this.apiPath + "/login_check", credentials, false)
          .then((data) => {
            if (data.token) {
              cookieManager.deleteCookie("studea-loginAttempts");
              this.setToken(data, this.apiPath, stayConnected);
              resolve({ success: true, token: data.token });
            }
          })
          .catch((res) => {
            this.invalidCredentials();
            if (attempts && attempts === this.authorizedLoginAttempts - 1) {
              res.message = i18n.global.t("error.login_blocked");
              res.messageType = "warning";
            }
            resolve(res);
          });
      }
    });
  }

  signIn(params, locale) {
    return new Promise((resolve) => {
      apiConnection
        .fetchApi("post", this.apiPath + "/" + locale + "/app/user/post/sign-in", params, false)
        .then((data) => {
          if (data.token) {
            this.setToken(data, this.apiPath);
            resolve({ success: true, token: data.token });
          }
        })
        .catch((res) => {
          resolve({ success: false, message: res.message, code: res.code });
        });
    });
  }

  resetPassword(username) {
    return apiConnection.post(URL_RESET_PASSWORD, { username: username }, false).then(() => {
      notificationManager.showNotification(
        "success",
        i18n.global.t("reset_password_confirm"),
        10000,
        "top-end",
        false,
        "-no-header",
      );
    });
  }

  newPassword(password, hash, router) {
    return apiConnection.post(URL_SEND_NEW_PASSWORD + "/" + hash, { password: password }, false).then((data) => {
      if (!data.hasError) {
        notificationManager.showNotification(
          "success",
          i18n.global.t("new_password_confirm"),
          10000,
          "top-end",
          false,
          "-no-header",
        );
        router.push({ name: "Login" });
      } else {
        notificationManager.showNotification(
          "error",
          i18n.global.t("reset_link_expired"),
          10000,
          "top-end",
          false,
          "-no-header",
        );
      }
    });
  }

  storeInitialToken() {
    const initialToken = window.localStorage.getItem("studea-token")
      ? window.localStorage.getItem("studea-token")
      : window.sessionStorage.getItem("studea-token");
    const initialRefreshToken = window.localStorage.getItem("studea-refreshToken")
      ? window.localStorage.getItem("studea-refreshToken")
      : window.sessionStorage.getItem("studea-refreshToken");

    const initialTokenExpirationDate = window.localStorage.getItem("studea-tokenExpirationDate")
      ? window.localStorage.getItem("studea-tokenExpirationDate")
      : window.sessionStorage.getItem("studea-tokenExpirationDate");

    const initialOldTrainingCourse = window.localStorage.getItem("studea-oldTrainingCourse");

    const initialOldTriad = window.localStorage.getItem("studea-oldTriad");

    window.sessionStorage.setItem("studea-initial-token", initialToken);
    window.sessionStorage.setItem("studea-initial-refreshToken", initialRefreshToken);
    if (initialOldTrainingCourse) {
      window.sessionStorage.setItem("studea-initial-oldTrainingCourse", initialOldTrainingCourse);
    }
    if (initialOldTriad) {
      window.sessionStorage.setItem("studea-initial-oldTriad", initialOldTriad);
    }
    if (initialTokenExpirationDate) {
      window.sessionStorage.setItem("studea-initial-tokenExpirationDate", initialTokenExpirationDate);
    }

    window.sessionStorage.removeItem("studea-token");
    window.sessionStorage.removeItem("studea-refreshToken");
    window.sessionStorage.removeItem("studea-tokenExpirationDate");
  }

  redirect(triadId = null) {
    const urlParams = new URLSearchParams(window.location.search);
    const referer = urlParams.get("referer");

    if (referer) {
      router.push(referer);
    } else {
      const user = useUserStore().user;
      if (triadId || (user.target === "triad" && user.targetParams?.triadId)) {
        localStorage.setItem("studea-oldTriad", triadId ? triadId : user.targetParams.triadId);
      }

      if (triadId) {
        router.push({ name: "Dashboard" });
      } else {
        switch (user.target) {
          default:
          case "training":
            router.push({ name: "TrainingCourseList" });
            break;
          case "admin":
            router.push({ name: "adminHome" });
            break;
          case "triad":
            router.push({ name: "Dashboard" });
            break;
          case "registration":
            this.getRegistrationRedirection(user.targetParams);
            break;
        }
      }
    }
  }

  getRegistrationRedirection(params, currentRouter = router) {
    const actorType = params.registrationActorType;
    const registrationCryptedId = params.registrationCryptedId;
    const groupCryptedId = params.groupCryptedId;

    if (registrationCryptedId && groupCryptedId) {
      currentRouter.push({
        name: "TrainingPreRegistration",
        params: { registration: registrationCryptedId, group: groupCryptedId, tab: 0 },
      });
      return;
    }

    switch (actorType) {
      case registrationConstante.REGISTRATION_ACTOR_UFA:
        currentRouter.push({ name: "TrainingList" });
        break;
      case registrationConstante.REGISTRATION_ACTOR_COMPANY:
        currentRouter.push({ name: "CompanyContractList" });
        break;
      case registrationConstante.REGISTRATION_ACTOR_APPRENTICE:
      default:
        currentRouter.push({ name: "ApprenticeContractList" });
        break;
    }
  }

  invalidCredentials() {
    let attempts = this.loginAttempts();
    if (!attempts) {
      attempts = 0;
    }
    attempts++;
    if (attempts <= this.authorizedLoginAttempts) {
      const expirationDate = new Date();
      expirationDate.setMinutes(expirationDate.getMinutes() + this.authorizedLoginAttempts);
      cookieManager.setCookie("studea-loginAttempts", attempts, {
        sameSite: "Strict",
        expires: expirationDate,
      });
    }
  }

  loginAttempts() {
    return cookieManager.getCookie("studea-loginAttempts");
  }

  getForm(type, actorType) {
    return apiConnection.get("/app/user/form/" + type, actorType).then((response) => response);
  }
}

export const loginManager = new LoginManager();
